import { onboardingManager } from "@core/service-container";
import { OnboardingStep, OnboardingStepsStatus, OnboardingStepStatus } from "@domain/onboarding/onboarding";
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { useObservable } from "micro-observables";
import React, { useCallback, useMemo } from "react";

interface OnboardingContextProps {
	programEditor: ProgramContentEditor;
	programStatus: ProgramStatus | undefined;
	children: React.ReactNode;
}

interface OnboardingContextValues {
	getExerciseCardOnboardings: (
		exerciseId: string,
		isDragging: boolean,
	) => {
		shouldDisplayAlternativeGuide1: boolean;
		shouldDisplayAlternativeGuide5: boolean;
		isAlternativeOnboardingGuide1Or5Displayable: boolean;
	};
}

export const OnboardingContext = React.createContext<OnboardingContextValues | null>(null);

export const OnboardingContextProvider: React.FC<OnboardingContextProps> = ({
	programEditor,
	programStatus: _programStatus,
	children,
}) => {
	// Mark intentionally unused props to satisfy lint rules without altering behavior
	if (_programStatus) {
		/* no-op */
	}
	useObservable(programEditor.firstExerciseDraggableId);
	useObservable(onboardingManager.onboardingStepsStatus);
	useObservable(programEditor.programMetadata);

	const getExerciseCardOnboardings = useCallback(
		(_exerciseId: string, _isDragging: boolean) => {
			// Touch params to avoid unused-var lint; logic remains always-false
			if (_exerciseId || _isDragging) {
				/* no-op */
			}
			return {
				shouldDisplayAlternativeGuide1: false,
				shouldDisplayAlternativeGuide5: false,
				isAlternativeOnboardingGuide1Or5Displayable: false,
			};
		},
		[],
	);

	const contextValue = useMemo(() => ({ getExerciseCardOnboardings }), [getExerciseCardOnboardings]);

	return (
		<OnboardingContext.Provider value={contextValue}>
			{children}
		</OnboardingContext.Provider>
	);
};

export const shouldDisplayOnboardingStep = (
	step: OnboardingStep,
	stepsStatus: OnboardingStepsStatus | undefined,
): boolean =>
	stepsStatus?.[step] === OnboardingStepStatus.AVAILABLE ||
	stepsStatus?.[step] === OnboardingStepStatus.DISMISSED_BUT_DISPLAYED;
